import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'cursor-pointer inline-flex shadow-lg select-none backdrop-blur-sm items-center justify-center rounded-md text-xs font-semibold ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-background hover:bg-primary/80',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/80',
        success: 'bg-success text-success-foreground hover:ring-success/80',
        outline:
          'text-halfmast-200 hover:bg-muted/20 ring-1 ring-inset ring-border',
        'outline-primary':
          'bg-transparent text-primary ring-1 ring-inset ring-primary hover:ring-primary/80',
        accent:
          'bg-accent/10 text-accent-foreground ring-1 ring-inset ring-accent-foreground/20 hover:ring-accent-foreground/80',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground shadow-none',
        wonka: 'bg-wonka-light text-white hover:bg-wonka-light/80',
        link: 'text-primary underline-offset-6 hover:underline decoration-2 shadow-none',
        glowing:
          'bg-primary text-background shadow-[0_0_20px_rgba(28,239,165,0.5)] hover:shadow-[0_0_20px_rgba(28,239,165,0.2)]',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
        cta: 'py-4 px-10 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
